import { Surgery } from 'common/types'
import { useUser } from 'app/User'
import { useIsMobile } from 'lib/utils/useIsMobile'
import { Box, Typography } from '@mui/material'
import './ProcedureListTable.scss'
import {
  ProcedureListTableProps,
  TShowSpecialRequestModal,
} from './ProcedureList.types'
import { formatSurgeryProviderName } from 'lib/utils/formatSurgeryProviderName'
import { useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import dayjs from 'lib/dayjs'
import { useState } from 'react'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'
import { TrayIcon } from 'assets/icons/TrayIcon'
import { useSetSurgeryStatusAsStarted } from 'lib/apollo/hooks'

export const useProcedureListTableLogic = (props: ProcedureListTableProps) => {
  const { events, showDate = false, sortByDate = false } = props

  const { enableSpecialRequest } = useOrganizationSettings()
  const flags = useFlags()
  const navigate = useNavigate()
  const { isNurse, isRep } = useUser()
  const hasItems = events && events?.length > 0
  const isMobile = useIsMobile()
  const [setStatus] = useSetSurgeryStatusAsStarted()

  const [isNotificationsOpen, setIsNotificationsOpen] = useState<{
    isOpen: boolean
    surgery?: Surgery
  }>({
    isOpen: false,
  })

  const hasNotifications = (surgery?: Surgery): boolean => {
    return (
      (surgery?.salesRepsNotified?.[0]?.salesReps?.length as number) > 0 ||
      false
    )
  }

  const handleStartSurgery = (surgery: Surgery) => {
    navigate(`/surgeries/${surgery?._id}`)
    if (surgery.status.name !== 'SCANNING') return
    setStatus({
      variables: {
        surgeryId: surgery._id,
      },
    })
  }

  const RoomInformation: React.FC<{
    surgery: Surgery
  }> = ({ surgery }) => {
    const isManuallyAdded = surgery.isManuallyAdded
    const earliestDateTime = surgery.procedures
      ?.map((proc) => proc.dateTime)
      .filter(Boolean)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0]

    const timeString = earliestDateTime
      ? dayjs(earliestDateTime).format('h:mm A')
      : ''

    return (
      <Box className="room" data-testid="room-number">
        {isManuallyAdded ? (
          <Box className="room-manually-added">
            <Typography className="room-manually-added-label">M</Typography>
          </Box>
        ) : null}

        <Box className="room-tray-icon">
          <TrayIcon color="white" />
        </Box>

        {timeString && flags.showSurgeryTime ? (
          <Box className="room-time">
            <Typography className="room-time-text">{timeString}</Typography>
          </Box>
        ) : (
          <Box className="room-time-placeholder" />
        )}

        {showDate && earliestDateTime && (
          <Typography className="room-date">
            {dayjs(earliestDateTime).format('MM/DD/YYYY')}
          </Typography>
        )}
      </Box>
    )
  }

  const PatientInformation: React.FC<{
    surgery: Surgery
    isNurse: boolean
  }> = ({ surgery, isNurse }) => (
    <Box className="patient" data-testid="patient-procedure-info">
      {isNurse && (
        <>
          <Typography fontSize="1rem" fontWeight="500">
            {`${surgery?.patient?.lastName.charAt(
              0
            )}, ${surgery?.patient?.firstName?.charAt(0)}`}
          </Typography>
          <br />
        </>
      )}
      {flags.procedureListSurgeryType && (
        <Typography>
          Surgery: {surgery?.procedures?.[0]?.description}
        </Typography>
      )}
      <Typography>Surgeon: {formatSurgeryProviderName(surgery)}</Typography>
    </Box>
  )

  const caseInformation: React.FC<{
    surgery: Surgery
    isNurse: boolean
  }> = ({ surgery, isNurse }) => (
    <>
      <RoomInformation surgery={surgery} />
      <PatientInformation surgery={surgery} isNurse={isNurse} />
    </>
  )

  const eventsArray = events && [...events]

  const sortSurgeries = (surgeries: Surgery[]) => {
    if (sortByDate) {
      return surgeries.sort((a: Surgery, b: Surgery) => {
        const aDateTime = a.procedures[0]?.dateTime || ''
        const bDateTime = b.procedures[0]?.dateTime || ''
        if (aDateTime && bDateTime) {
          return dayjs(aDateTime).isBefore(dayjs(bDateTime)) ? -1 : 1
        }
        return 0
      })
    }

    return surgeries.sort((a: Surgery, b: Surgery) => {
      const roomA = a?.visit?.location?.room || ''
      const roomB = b?.visit?.location?.room || ''
      if (roomA < roomB) {
        return -1
      }
      if (roomA > roomB) {
        return 1
      }

      const aDateTime = a.procedures[0]?.dateTime || ''
      const bDateTime = b.procedures[0]?.dateTime || ''
      if (aDateTime && bDateTime) {
        return dayjs(aDateTime).isBefore(dayjs(bDateTime)) ? -1 : 1
      }
      return 0
    })
  }

  const filteredSurgeries = eventsArray?.filter(
    (surgery: Surgery) => surgery.procedures.length > 0
  )

  const sortedSurgeries = sortSurgeries(filteredSurgeries ?? [])

  // feat: special request
  const [showSpecialRequestModal, setShowSpecialRequestModal] =
    useState<TShowSpecialRequestModal>({
      showModal: null,
      surgeryId: null,
      surgeryStatus: null,
    })

  const [loadingSpecialRequests, setLoadingSpecialRequests] = useState(false)

  const handleSpecialRequest = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    surgeryId: string,
    surgeryStatus: Surgery['status']['name']
  ) => {
    e.stopPropagation()
    if (e.currentTarget.id === 'addSpecialRequest') {
      setShowSpecialRequestModal({ showModal: 'add', surgeryId, surgeryStatus })
    } else if (e.currentTarget.id === 'showSpecialRequests') {
      setLoadingSpecialRequests(true)
      setShowSpecialRequestModal({
        showModal: 'show',
        surgeryId,
        surgeryStatus,
      })
    }
  }

  const handleCloseSpecialRequest = () => {
    setShowSpecialRequestModal({
      showModal: null,
      surgeryId: null,
      surgeryStatus: null,
    })
  }

  const [editModalSurgery, setEditModalSurgery] = useState<Surgery | null>(null)

  const handleOpenOptionsModal = (surgery: Surgery) => {
    setEditModalSurgery(surgery)
  }

  const handleCloseEditModal = () => {
    setEditModalSurgery(null)
  }

  return {
    flags,
    isRep,
    isNurse,
    hasItems,
    isMobile,
    caseInformation,
    sortedSurgeries,
    isNotificationsOpen,
    enableSpecialRequest,
    loadingSpecialRequests,
    showSpecialRequestModal,
    hasNotifications,
    handleStartSurgery,
    handleSpecialRequest,
    setIsNotificationsOpen,
    handleCloseSpecialRequest,
    setLoadingSpecialRequests,
    setShowSpecialRequestModal,
    editModalSurgery,
    handleOpenOptionsModal,
    handleCloseEditModal,
  }
}
